import * as React from 'react';
import gettext from '@sdk/lang/gettext';
import useAuth from '@sdk/authentication/useAuth';
import { Feature } from '@sdk/common/features';
import { Sections } from '@sdk/components/ui/anchorMenu/createSections';
import Row from '@sdk/components/ui/spacing/Row';
import Text14 from '@sdk/components/ui/typography/Text14';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';

import usePremiumGuard from '../premiumGuard/usePremiumGuard';
import PremiumGuard from '../premiumGuard/PremiumGuard';
import getPremiumTooltipId from '../premiumGuard/getPremiumTooltipId';
import { PremiumModalNames } from '../premiumGuard/constants/modalNames';

import type { UseShiftAlarmsType } from './components/notifications/shiftAlarms/useShiftAlarms';
import SettingsHeader from './components/SettingsHeader';
import ShiftAlarmsNotifications from './components/notifications/ShiftAlarmsNotifications';
import DashboardNotification from './components/notifications/DashboardNotification';
import MessageNotification from './components/notifications/MessageNotification';
import NewsfeedNotification from './components/notifications/NewsfeedNotification';
import DailyEmailReport from './components/notifications/DailyEmailReport';
import TimeclockNotificationManagerSettings from './components/notifications/TimeclockNotificationManagerSettings';
import TimeclockNotificationEmployeeSettings from './components/notifications/TimeclockNotificationEmployeeSettings';
import { ClientSettingsType, SetClientSettings } from './types';
import { useUserSettings } from './UserSettingsContext';
import SettingsSwitcherRow from './components/SettingsSwitcherRow';

type Props = {
  sectionsConfig: Sections;
  clientSettings: ClientSettingsType;
  setClientSettings: SetClientSettings;
} & UseShiftAlarmsType;

const NotificationsScreen: React.FC<React.PropsWithChildren<Props>> = ({
  sectionsConfig,
  setClientSettings,
  clientSettings,
  alarmsModel,
  addAnotherAlarm,
  setAlarmsModel,
  canAddAnotherAlarm,
  removeAlarm,
  onChangeAlarmUnit,
  onChangeAlarmValue,
}) => {
  const { currentUser, isOrgFeatureEnabled } = useAuth();
  const { getPremiumGuardConfig, tooltipRefs } = usePremiumGuard();
  const { userSettings, onUserSettingsChange } = useUserSettings();

  const showMessagesSection =
    !currentUser.orgSettings.messagesFeatureDisabled && isOrgFeatureEnabled(Feature.messages);
  const showTimeClock =
    (currentUser.orgSettings.clockInFromWebAndApps || currentUser.orgSettings.clockInEnabled) &&
    isOrgFeatureEnabled(Feature.clockIn);

  const renderTimeClock = (): JSX.Element => {
    if (currentUser.isManager) {
      return (
        <>
          <SettingsHeader
            ref={sectionsConfig.refs.dailyReport}
            title={gettext('Daily email report')}
          />
          <DailyEmailReport />

          {showTimeClock && (
            <PremiumGuard
              hasAccess={isOrgFeatureEnabled(Feature.clockIn)}
              premiumGuardModalName={PremiumModalNames.clockIn}
              tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.clockIn)]}>
              <>
                <SettingsHeader
                  withSeparator
                  title={gettext('Timeclock notification')}
                  premiumGuard={getPremiumGuardConfig(Feature.clockIn)}
                  ref={sectionsConfig.refs.timeClock}
                />
                <TimeclockNotificationManagerSettings />
              </>
            </PremiumGuard>
          )}
        </>
      );
    }

    if (showTimeClock) {
      return (
        <PremiumGuard
          hasAccess={isOrgFeatureEnabled(Feature.clockIn)}
          premiumGuardModalName={PremiumModalNames.clockIn}
          tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.clockIn)]}>
          <>
            <SettingsHeader
              withSeparator
              title={gettext('Timeclock notification')}
              premiumGuard={getPremiumGuardConfig(Feature.clockIn)}
              ref={sectionsConfig.refs.timeClock}
            />
            <TimeclockNotificationEmployeeSettings />
          </>
        </PremiumGuard>
      );
    }

    return null;
  };

  return (
    <>
      <SettingsHeader ref={sectionsConfig.refs.shiftAlarms} title={gettext('Shift alarms')} />
      <ShiftAlarmsNotifications
        alarmsModel={alarmsModel}
        addAnotherAlarm={addAnotherAlarm}
        setAlarmsModel={setAlarmsModel}
        canAddAnotherAlarm={canAddAnotherAlarm}
        removeAlarm={removeAlarm}
        onChangeAlarmUnit={onChangeAlarmUnit}
        onChangeAlarmValue={onChangeAlarmValue}
      />

      <SettingsHeader
        ref={sectionsConfig.refs.dashboard}
        title={gettext('Dashboard notifications')}
      />
      <DashboardNotification />

      {showMessagesSection && (
        <>
          <SettingsHeader
            ref={sectionsConfig.refs.messages}
            title={gettext('Message notifications')}
          />
          <MessageNotification
            setClientSettings={setClientSettings}
            clientSettings={clientSettings}
          />
        </>
      )}

      {!currentUser.orgSettings.newsfeedFeatureDisabled && (
        <>
          <SettingsHeader
            ref={sectionsConfig.refs.newsfeed}
            title={gettext('Newsfeed notifications')}
          />
          <NewsfeedNotification
            setClientSettings={setClientSettings}
            clientSettings={clientSettings}
          />
        </>
      )}

      {renderTimeClock()}

      {currentUser.isAdmin ? (
        <>
          <SettingsHeader title={gettext('Employee activity notifications')} />
          <SettingsSwitcherRow
            mTop={5}
            value={!!userSettings.adminNotificationsEnabled}
            onChange={(value): void => onUserSettingsChange({ adminNotificationsEnabled: value })}
            subtitle={gettext(
              'Receive notifications when an employee adds time off and unavailability, and when an employee applies for a shift, offers or swaps a shift, and when a shift is accepted or denied'
            )}
          />
          <Row mTop={20}>
            <Text14 themeColor={ThemeColorType.gray}>
              {gettext('When this is disabled,')}&nbsp;
            </Text14>
            <Text14 bold themeColor={ThemeColorType.gray}>
              {gettext('managers will still receive these notifications')}
            </Text14>
          </Row>
        </>
      ) : null}
    </>
  );
};

export default NotificationsScreen;
